import { observer } from 'mobx-react';
import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import CopyToClipboardButton from '../CopyToClipboardButton';

import { VivaOnboardingStatus } from '../../model/PaymentMethod';
import {
  useIntegrationsList,
  useVivaIntegrationStatus,
} from '../../stores/FetcherStores/integrationFetcher';
import useCompanyId from '../../hooks/useCompanyId';

interface VivaWalletStatusProps {
  integrationId: string;
}

const VivaWalletStatus = observer(({ integrationId }: VivaWalletStatusProps) => {
  const companyId = useCompanyId();
  const { data: integrations } = useIntegrationsList(companyId);
  const { mutate: fetchIntegrationStatus, isLoading: isFetchingIntegrationStatus } =
    useVivaIntegrationStatus();

  const vivaIntegration = integrations?.find(
    (integration) => integration.type === 'VIVA' && integration.id === integrationId,
  );

  const { t } = useTranslation();

  if (!vivaIntegration) {
    return null;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography sx={{ marginRight: 1 }} fontWeight={700}>
            {t('paymethodScreen.status')}:
            {vivaIntegration.onboardingStatus === VivaOnboardingStatus.VERIFIED &&
              t('vivaStatus.verified')}
            {vivaIntegration.onboardingStatus === VivaOnboardingStatus.IN_PROGRESS &&
              t('vivaStatus.inProgress')}
          </Typography>
        </Box>

        <Button
          disabled={isFetchingIntegrationStatus}
          size="medium"
          variant="contained"
          onClick={() => {
            fetchIntegrationStatus(companyId, integrationId);
          }}
        >
          {isFetchingIntegrationStatus ? (
            <Box component="span" sx={{ display: 'flex' }}>
              <CircularProgress size={20} />
            </Box>
          ) : (
            t('paymethodScreen.getStatus')
          )}
        </Button>
      </Box>

      {vivaIntegration.onboardingStatus === VivaOnboardingStatus.IN_PROGRESS && (
        <TextField
          id="onboardingUrl"
          label={t('paymethodScreen.onboardingLink')}
          variant="outlined"
          value={vivaIntegration.onboardingUrl}
          sx={{ mt: 2, mb: 2 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <CopyToClipboardButton text={vivaIntegration.onboardingUrl} alignment="end" />
              </InputAdornment>
            ),
          }}
          fullWidth
        />
      )}
    </Box>
  );
});

export default VivaWalletStatus;
